import { useContext, useState } from "react";
import styled from "styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "components/core/icon";
import { AuthStateContext, AuthDispatchContext, signOut } from "contexts/auth";
import Typography from "components/core/typography";
import Button from "./core/button";
import Container from "./core/container";
import Dropdown from "./core/dropdown";
import { COLORS } from "styled/colors";
import Chip from "./core/chip";

const Header = () => {
  const { user, isLoggedIn } = useContext(AuthStateContext);
  const dispatch = useContext(AuthDispatchContext);
  const [isVisible, setIsVisible] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const activeSubscription = user?.subscriptions?.find(
    (sub) => sub.status === "active"
  );
  const isPremium = activeSubscription?.type === "premium";

  const handleToggleDropdown = (isOpen) => {
    setIsVisible(isOpen);
  };

  const handleNavigate = (page) => {
    navigate(page);
    handleToggleDropdown(false);
  };

  const handleLogout = () => {
    queryClient.clear();
    signOut(dispatch);
  };
  return (
    <HeaderWrapper>
      <NavLink className="logo" to="/">
        <img src="/logo-horizontal-white.svg" alt="SpendSights" />
      </NavLink>

      <Dropdown
        isOpen={isVisible}
        onDropdownChange={handleToggleDropdown}
        trigger={
          <>
            {isLoggedIn ? (
              <MenuNavDropDown onClick={() => handleToggleDropdown()}>
                <Icon name="user-solid-circle" />
              </MenuNavDropDown>
            ) : (
              <MenuNavLink to="/login">Login/Register</MenuNavLink>
            )}
          </>
        }
        content={
          <Container padding={[0, 0, 0, 0]}>
            <Typography margin={[0, 0, 4, 16]} className="name">
              {user?.name || "Welcome"}
            </Typography>
            <Typography
              variant="small"
              margin={[0, 0, 16, 16]}
              display="block"
              color={COLORS.text.gray700}
              className="email"
            >
              {user?.email}
            </Typography>
            <MenuButton
              variant="nude"
              size="large"
              onClick={() => handleNavigate("/settings")}
              textAlign="left"
            >
              Settings
            </MenuButton>
            <MenuButton
              variant="nude"
              size="large"
              onClick={() => handleNavigate("/settings/profile")}
              textAlign="left"
            >
              Profile
            </MenuButton>
            <MenuButton
              variant="nude"
              size="large"
              onClick={() => handleNavigate("/settings/subscription")}
              textAlign="left"
            >
              Subscription
              <MenuButtonChip
                title={isPremium ? "Premium" : "Freemium"}
                size="small"
                variant="filled"
                color={isPremium ? "warning" : "error"}
                className={isPremium ? "premium" : "freemium"}
              />
            </MenuButton>
            <MenuButton
              variant="nude"
              size="large"
              onClick={() => handleLogout()}
              textAlign="left"
            >
              Logout
            </MenuButton>
          </Container>
        }
      />
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #007aff;
  padding: 16px;
  @media (min-width: 768px) {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 98;
  }
  .name {
    word-break: break-all;
  }
  .email {
    word-break: break-all;
    color: #999;
  }
  .logo {
    display: block;
    img {
      width: 160px;
      display: block;
    }
  }
`;

const MenuNavLink = styled(NavLink)`
  color: #fff;
  i {
    font-size: 24px;
    line-height: 26px;
  }
`;

const MenuButton = styled(Button)`
  height: 40px;
  &:hover {
    background: ${COLORS.background.gray200};
  }
`;

const MenuButtonChip = styled(Chip)`
  margin-left: 6px;
  border-width: 0;
  text-shadow: 0 2px 2px rgba(0, 0, 0, 0.15);
  &.premium {
    background: linear-gradient(135deg, #ffd700, #ffb347, #ff6b35);
  }
  &.freemium {
    background: linear-gradient(135deg, #bdc3c7, #2c3e50);
  }
`;

const MenuNavDropDown = styled.div`
  display: flex;
  align-items: center;
  color: #fff;
  cursor: pointer;
  i {
    font-size: 24px;
  }
`;

export default Header;
